import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveMachinesQuery } from '../../../../hooks/queries/useMachinesQuery';
import { DataItem, updateDailyMachine } from '../../../../services/apiSLTP/activities';
import { Machine } from '../../../../services/apiSLTP/materials/machines';
import { createDailyMachine, DailyMachine } from '../../../../services/apiSLTP/planning';
import { removeDuplicates } from '../../../../utils/array';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getMaterialStateInfos, getPriceTypeInfos, materialState, priceTypeEnum, stepModes, systemConfigKeyEnum } from '../../../../utils/enums';
import DropDown, { OptionType } from '../../../DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';
import ToggleLine from '../../../ToggleLine';
import CustomTextInput from '../../../CustomTextInput';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';

interface ManageDailyMachineModalProps extends ModalProps {
    activityId: string;
    canChooseActivity?: boolean;
    refetch: () => void;
    dailyMachine: DailyMachine | null;
    machine: Machine | null;
    activities: DataItem[];
    site?: { name: string; id: string };
    siteName?: string;
    createTransfer: boolean;
    stepMode?: number;
}

const ManageDailyMachineModal: React.FC<ManageDailyMachineModalProps> = ({
    open,
    handleClose,
    refetch,
    activityId,
    dailyMachine,
    canChooseActivity = false,
    activities,
    machine,
    site,
    siteName,
    createTransfer,

    stepMode,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyMachineId, setCurrentDailyMachineId] = useState(dailyMachine?._id || '');
    const [selectedMachine, setSelectedMachine] = useState({ label: '', value: '' });
    const [selectedSite, setSelectedSite] = useState({ label: '', value: '' });
    const [selectedActivity, setSelectedActivity] = useState({ label: '', value: '' });
    const [isCreateTransfer, setIsCreateTransfer] = useState(createTransfer);
    const [selectedState, setSelectedState] = useState<{
        label: string;
        value: string;
    }>({ label: capitalizeFirstLetter(t('present')), value: materialState.PRESENT.toString() });

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.NEW))), value: materialState.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.PRESENT))), value: materialState.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.REMOVE))), value: materialState.REMOVE.toString() },
    ];
    const [price, setPrice] = useState(typeof dailyMachine?.price === 'number' ? dailyMachine?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailyMachine?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeInfos(dailyMachine?.priceType))),
                  value: (dailyMachine?.priceType).toString(),
              }
            : null
    );

    const { data: dataMachines } = useActiveMachinesQuery({});

    const postDailyMachine = async () => {
        const response = await createDailyMachine(
            activityId || selectedActivity.value,
            selectedMachine.value,
            parseInt(selectedState.value),
            isCreateTransfer,
            price ? parseFloat(price) : null,
            priceType ? parseInt(priceType?.value) : null
        );
        setCurrentDailyMachineId(response.data._id);
        refetch();
    };

    const patchDailyMachine = async () => {
        const response = await updateDailyMachine(
            currentDailyMachineId,
            {
                machineId: selectedMachine.value,
                state: parseInt(selectedState.value),
                createTransfer: isCreateTransfer,
                price: price ? parseFloat(price) : null,
                priceType: priceType ? parseInt(priceType?.value) : null,
            },
            {
                syncTransfers: true,
            }
        );
        refetch();
    };

    useEffect(() => {
        if (dailyMachine) {
            setSelectedMachine({ label: dailyMachine.machineId?.designation || '', value: dailyMachine.machineId?._id || '' });
            setSelectedState({
                label: capitalizeFirstLetter(t(getMaterialStateInfos(dailyMachine.state))) || '',
                value: dailyMachine.state.toString() || '',
            });
        } else if (machine) {
            setSelectedMachine({ label: machine?.designation || '', value: machine?._id || '' });
        }
        if (canChooseActivity && site) {
            setSelectedSite({ label: site?.name, value: site?.id });
        }
    }, []);

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('machine')}
            subTitle={!!currentDailyMachineId.length ? t('update') : t('add')}
            description={siteName}
            okText={t('validate')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentDailyMachineId.length ? postDailyMachine() : patchDailyMachine();
            }}
            disabledOk={!selectedMachine.value.length || (canChooseActivity && !selectedActivity.value.length)}
        >
            {canChooseActivity && (
                <>
                    <DropDown
                        selectedItem={selectedSite}
                        items={
                            removeDuplicates(
                                activities.map((activity) => activity.siteId),
                                ['_id']
                            ).map((site) => ({
                                label: site.name,
                                value: site._id,
                            })) || []
                        }
                        handleChange={(event, value) => {
                            setSelectedActivity({ label: '', value: '' });
                            setSelectedSite({ value: value?.value || '', label: value?.label || '' });
                        }}
                        inputLabel={t('site')}
                        required
                    />
                    {!!selectedSite.value.length && (
                        <DropDown
                            selectedItem={selectedActivity}
                            items={
                                activities
                                    .filter((activity) => activity.siteId._id === selectedSite.value)
                                    .map((activity) => ({
                                        label: activity.reportId.userId.firstName + ' ' + activity.reportId.userId.lastName,
                                        value: activity._id,
                                    })) || []
                            }
                            handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                            inputLabel={t('person')}
                            required
                        />
                    )}
                </>
            )}
            <DropDown
                selectedItem={selectedMachine}
                items={
                    dataMachines?.map((machine) => ({
                        label: machine.designation,
                        value: machine._id,
                        group: machine.machineTypeId.designation,
                    })) || []
                }
                handleChange={(event, value) => setSelectedMachine({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('machine')}
                grouped
                required
            />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('state')}
                required
            />
            {stepMode !== stepModes.VALIDATE && (
                <ToggleLine
                    checked={isCreateTransfer}
                    onChange={(checked) => setIsCreateTransfer(checked)}
                    title={capitalizeFirstLetter(t('createTransfer'))}
                    disabled={parseInt(selectedState.value) === materialState.PRESENT}
                />
            )}
            {stepMode === stepModes.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                        placeholder={`${t('default')} - ${dailyMachine?.machineId.price ?? dailyMachine?.machineId.machineTypeId.price}`}
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeInfos(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                        placeholder={`${t('default')} - ${capitalizeFirstLetter(t(getPriceTypeInfos(dailyMachine?.machineId.priceType ?? dailyMachine?.machineId.machineTypeId.priceType)))}`}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailyMachineModal;
